import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";

import Input from "components/Reusables/Input";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons/faChevronRight";

import IconButton from "@components/Reusables/IconButton";
import { colors } from "utils/styles";
import SearchApi from "@utils/api/SearchApi";
import LoadingSpinner from "@components/Reusables/LoadingSpinner";
import Text from "@components/Reusables/Text";
import Title from "@components/Reusables/Title";
import ButtonGroup from "@components/Reusables/ButtonGroup";
import DynamicLink from "@components/Reusables/DynamicLink";
import { scrollTo } from "utils/helpers";

export default function SearchInputBar(props) {
	const { setShowSearch } = props;

	const [searchValue, setSearchValue] = useState("");
	const [suggestions, setSuggestions] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [searchResults, setSearchResults] = useState([]);
	const searchPage = useRef(0);
	const timeoutId = useRef(0);
	const mode = useRef(0); // 0 for suggestions search, 1 for results
	const cancelSuggestionsSearch = useRef(false);

	const router = useRouter();

	useEffect(() => {
		function handleEscapeKey(event) {
			if (event.code === "Escape") {
				setShowSearch(false);
			}
		}

		const handleRouteChange = (url) => {
			setShowSearch(false);
		};

		router.events.on("routeChangeStart", handleRouteChange);

		document.addEventListener("keydown", handleEscapeKey);
		return () => {
			document.removeEventListener("keydown", handleEscapeKey);
			router.events.off("routeChangeStart", handleRouteChange);
		};
	}, []);

	const updateSearchValue = (val) => {
		clearTimeout(timeoutId.current);
		mode.current = 0;

		if (val.length === 0) {
			cancelSuggestionsSearch.current = true;
			setSuggestions([]);
			setSearchValue(val);
			return;
		}

		setSearchValue(val);

		timeoutId.current = setTimeout(function () { autoSuggest(val); }, 90);
	}

	const autoSuggest = async (val) => {
		try {
			searchPage.current = 0;

			let autoSuggestResp = await SearchApi.autoSuggest(val);

			if (cancelSuggestionsSearch.current === false) {
				const suggestions = autoSuggestResp?.results?.map(x => x.query) ?? [];
				setSuggestions(suggestions);
			}
			else {
				cancelSuggestionsSearch.current = false;
				setSuggestions([]);
			}
		}
		catch (ex) {
			setIsLoading(false);
			setSuggestions([]);
		}
	};

	const handleSubmit = async () => {
		try {
			setSuggestions([]);
			setIsLoading(true);

			const resp = await SearchApi.search({ value: searchValue, page: searchPage.current });
			const results = resp.web?.results && resp.web.results.length > 0 ? resp.web.results : [];
			if (results.length === 0) {
				searchPage.current = 0;
			}
			setSearchResults(results);

			mode.current = 1;
			
			setIsLoading(false);
		}
		catch (err) {
			searchPage.current = 0;
			setSearchResults([]);
			setIsLoading(false);
		}
	};

	const NextPrevBtns = () => {
		const searchInput = document.querySelector("#search-input");
		return (
			<Row>
				<Col xs="6">
					{searchPage.current > 0 && (
						<ButtonGroup
							additionalClasses="px-3"
							groupClasses="justify-content-center"
							buttonWidth={{ xs: { offset: 1, span: "auto" } }}
							btn1={{
								text: (
									<>
										<FontAwesomeIcon icon={faChevronLeft} className="w-10px me-2 inline-flex" />{" "}
										{` Previous`}
									</>
								),
							}}
							onClick={() => {
								scrollTo(searchInput);
								searchPage.current = searchPage.current - 1;
								handleSubmit();
							}}
						/>
					)}
				</Col>
				<Col xs="6">
					{searchResults.length === 10 && searchPage.current < 9  && (
						<ButtonGroup
							additionalClasses="px-3"
							groupClasses="justify-content-center"
							buttonWidth={{ xs: { offset: 0, span: "auto" } }}
							btn1={{
								text: (
									<>
										{`Next`}{" "}
										<FontAwesomeIcon icon={faChevronRight} className="w-10px ms-2 inline-flex" />
									</>
								),
							}}
							onClick={() => {
								scrollTo(searchInput);
								searchPage.current = searchPage.current + 1;
								handleSubmit();
							}}
						/>
					)}
				</Col>
			</Row>
		);
	};


	return (
		<div id="search-input" className="row g-0 search-input align-items-center myShow">
			<Col md={{ offset: 2, span: 6 }} lg={{ offset: 2, span: 6 }} className="">
				<Input
					value={searchValue}
					onChange={(e) => {
						updateSearchValue(e.target.value);
						setSearchResults([]);
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							handleSubmit();
						}
					}}
					label="Keyword or topic..."
				/>
			</Col>
			<Col xs={6} md={{ span: 2 }} className="">
				<IconButton
					isDisabled={!searchValue}
					onClick={() => {
						handleSubmit();
					}}
					outlineColor={colors.yellow}
					groupClasses="ms-2"
					button1Variant="light"
					label={"Search"}
				/>
			</Col>
			<Col xs={6} md={{ span: 2 }} className="">
				<IconButton
					onClick={() => {
						setShowSearch(false);
					}}
					label="Search"
					icon="close"
					button1Variant="light"
				/>
			</Col>

			{(isLoading || searchValue.length > 0 || searchResults.length > 0 || suggestions.length > 0) &&
				<div className="search-results p-2 p-md-4">
					{/*<span>Debug | searchValue.Length: {searchValue.length} | suggestions.length: {suggestions.length} | searchResults.length: {searchResults.length} | searchPage: { searchPage.current}</span>*/}
					{isLoading && <LoadingSpinner />}
					{!isLoading && searchValue.length > 0 && mode.current === 1 && searchResults.length === 0 && searchPage.current === 0 && (<Col>No results were found for '{searchValue}'</Col>)}
					{!isLoading && searchValue.length > 0 && searchResults.length === 0 && suggestions.length > 0 && (
						<>
							{suggestions.map((suggestion, key) => {
								return (
									<React.Fragment>
										<div
											className="clickable hoverable p-2"
											onClick={() => {
												updateSearchValue(suggestion);
												setSuggestions([]);
												handleSubmit();
											}}
										>
											<Text text={suggestion} size={14} marginBottom="0" />
											{/* <p key={key}>{suggestion}</p> */}
										</div>
										{key < suggestions.length - 1 && <hr />}
									</React.Fragment>
								);
							})}
						</>
					)}
					{!isLoading && searchValue.length > 0 && searchResults.length > 0 && (
						<>
							<Col className="justify-content-center d-flex">Page {searchPage.current + 1}</Col>
							<NextPrevBtns />

							{searchResults.map((item, count) => {
								return (
									<Row key={count} className="search-results p-2 g-0 justify-content-center">
										<Col className="" md={10}>
											<Row>
												<Col className="" xs="8">
													<DynamicLink href={item.url}>
														<Title className="mb-0" text={item.title.includes("undefined") ? "AAPL" : item.title} element="h4" />
													</DynamicLink>
												</Col>
												{item.thumbnail?.original && !item.thumbnail.original.includes("undefined") && (
													<Col xs="4" className="w- ">
														<img height={"50px"} src={item.thumbnail.original.replace("&amp;", "&")} />
													</Col>
												)}
												<Col className="" md="auto">
													<Text marginBottom="" text={item.description} size="14" />
												</Col>
												{/* <Text text={item.datePublished} size="12" /> */}
												<hr />
											</Row>
										</Col>
									</Row>
								);
							})}

							<NextPrevBtns />
						</>
					)}
				</div>
			}
			<style jsx="true">{`
				hr {
					margin: 0;
				}
				.search-results {
					background: ${colors.lightGray};
					box-shadow: 0 9px 11px -7px #0000002e;
					border: 2px solid ${colors.dark};
					margin-top: 0.4em;
				}
				.search-input {
					position: absolute;
					margin-top: 1em;

					z-index: 100;
					height: auto;
					top: 0px;
					bottom: 0px;
					right: 0px;
					left: 0px;
					box-shadow: 0 9px 11px -7px #0000002e;
					border: 1px solid ${colors.lightGray};
				}
			`}</style>
		</div>
	);
}
